import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Layout from '@/components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  DollarSign, TrendingUp, Users, Package, Repeat, Zap, CreditCard,
  Fuel, Shield, Gift, ArrowUpRight, Target, Crown,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STREAM_ICON = {
  'Marketplace Fees': Package,
  'Subscriptions': Crown,
  'Quick Pay': Zap,
  'Factoring': CreditCard,
  'Fuel Card Cashback': Fuel,
  'Insurance': Shield,
  'Referrals (paid out)': Gift,
};

const fmt = (n) => `$${Math.round(n || 0).toLocaleString()}`;
const fmt2 = (n) => `$${(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const RevenueCommandCenter = () => {
  const [overview, setOverview] = useState(null);
  const [series, setSeries] = useState([]);
  const [leaderboard, setLeaderboard] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const [a, b, c] = await Promise.all([
          axios.get(`${API}/revenue/overview`),
          axios.get(`${API}/revenue/timeseries?days=30`),
          axios.get(`${API}/revenue/leaderboard?limit=5`),
        ]);
        if (alive) {
          setOverview(a.data);
          setSeries(b.data.series || []);
          setLeaderboard(c.data);
        }
      } catch (e) {
        console.error(e);
      } finally {
        if (alive) setLoading(false);
      }
    };
    load();
    const id = setInterval(load, 10000);
    return () => { alive = false; clearInterval(id); };
  }, []);

  if (loading || !overview) {
    return <Layout><div className="p-8 text-center text-muted-foreground">Loading revenue intelligence...</div></Layout>;
  }

  const t = overview.totals;
  const maxFees = Math.max(...series.map(s => s.fees), 1);
  const maxStream = Math.max(...overview.streams.map(s => Math.abs(s.amount_30d)), 1);

  return (
    <Layout>
      <div className="p-4 lg:p-8 space-y-6" data-testid="revenue-page">
        {/* HERO */}
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-3xl lg:text-4xl font-bold flex items-center gap-3">
              <DollarSign className="w-8 h-8 text-green-400" />
              Revenue Command Center
            </h1>
            <p className="text-muted-foreground text-sm mt-1">
              Every dollar across every stream — in real time.
            </p>
          </div>
          <Badge className="bg-green-500/20 text-green-300 border-green-500/30 text-base px-4 py-1">
            Take Rate: {t.take_rate_pct}%
          </Badge>
        </div>

        {/* TOP-LINE STATS */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <Card data-testid="stat-30d">
            <CardContent className="p-5">
              <div className="text-xs uppercase tracking-wider text-muted-foreground mb-1">Revenue 30d</div>
              <div className="text-3xl font-bold text-green-400">{fmt(t.revenue_30d)}</div>
              <div className="text-xs text-muted-foreground mt-1">{fmt(t.revenue_today)} today · {fmt(t.revenue_7d)} this week</div>
            </CardContent>
          </Card>
          <Card data-testid="stat-gmv">
            <CardContent className="p-5">
              <div className="text-xs uppercase tracking-wider text-muted-foreground mb-1">GMV 30d</div>
              <div className="text-3xl font-bold">{fmt(t.gmv_30d)}</div>
              <div className="text-xs text-muted-foreground mt-1">{fmt2(t.avg_revenue_per_load)} avg/load fee</div>
            </CardContent>
          </Card>
          <Card data-testid="stat-mrr">
            <CardContent className="p-5">
              <div className="text-xs uppercase tracking-wider text-muted-foreground mb-1 flex items-center gap-1"><Repeat className="w-3 h-3" /> MRR</div>
              <div className="text-3xl font-bold text-blue-400">{fmt(t.mrr)}</div>
              <div className="text-xs text-muted-foreground mt-1">{fmt(t.arr)} ARR · {overview.subscriptions.active} active</div>
            </CardContent>
          </Card>
          <Card data-testid="stat-projection">
            <CardContent className="p-5">
              <div className="text-xs uppercase tracking-wider text-muted-foreground mb-1 flex items-center gap-1"><TrendingUp className="w-3 h-3" /> Annualized</div>
              <div className="text-3xl font-bold text-amber-400">{fmt(overview.projections.annualized)}</div>
              <div className="text-xs text-emerald-400 mt-1">{fmt(overview.projections.next_12_months_at_2x_growth)} @ 2× growth</div>
            </CardContent>
          </Card>
        </div>

        {/* CHART */}
        <Card data-testid="revenue-chart">
          <CardHeader>
            <CardTitle className="flex items-center gap-2"><TrendingUp className="w-5 h-5" /> Daily Revenue · 30d</CardTitle>
          </CardHeader>
          <CardContent>
            {series.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">No transactions yet. Engage Autopilot to start.</div>
            ) : (
              <div className="flex items-end gap-1 h-48">
                {series.map((s) => (
                  <div key={s.date} className="flex-1 flex flex-col items-center justify-end group" title={`${s.date}: ${fmt(s.fees)} fees · ${s.loads} loads`}>
                    <div className="text-[10px] text-muted-foreground opacity-0 group-hover:opacity-100 mb-1">{fmt(s.fees)}</div>
                    <div
                      className="w-full bg-gradient-to-t from-green-500 to-emerald-300 rounded-t hover:opacity-80 transition-opacity min-h-[2px]"
                      style={{ height: `${(s.fees / maxFees) * 100}%` }}
                    />
                    <div className="text-[9px] text-muted-foreground mt-1 rotate-45 origin-left whitespace-nowrap">{s.date.slice(5)}</div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* REVENUE STREAMS */}
        <Card data-testid="revenue-streams">
          <CardHeader>
            <CardTitle className="flex items-center gap-2"><Target className="w-5 h-5" /> Revenue Streams (30d)</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {overview.streams.map((s) => {
              const Icon = STREAM_ICON[s.name] || DollarSign;
              const width = `${(Math.abs(s.amount_30d) / maxStream) * 100}%`;
              return (
                <div key={s.name} className="space-y-1.5">
                  <div className="flex items-center justify-between text-sm">
                    <div className="flex items-center gap-2">
                      <Icon className="w-4 h-4" style={{ color: s.color }} />
                      <span className="font-medium">{s.name}</span>
                      <span className="text-xs text-muted-foreground">· margin {s.margin}</span>
                    </div>
                    <div className="flex items-center gap-3 text-xs text-muted-foreground">
                      <span>{s.count} txns</span>
                      <span className="font-bold text-base text-foreground" style={{ color: s.amount_30d >= 0 ? '#22c55e' : '#ef4444' }}>
                        {s.amount_30d >= 0 ? '' : '-'}{fmt(Math.abs(s.amount_30d))}
                      </span>
                    </div>
                  </div>
                  <div className="h-2 rounded-full bg-muted overflow-hidden">
                    <div className="h-full rounded-full transition-all" style={{ width, backgroundColor: s.color }} />
                  </div>
                </div>
              );
            })}
          </CardContent>
        </Card>

        {/* LEADERBOARD */}
        {leaderboard && (
          <div className="grid lg:grid-cols-2 gap-6">
            <Card data-testid="top-brokers">
              <CardHeader><CardTitle className="flex items-center gap-2"><Users className="w-5 h-5" /> Top Brokers (by GMV)</CardTitle></CardHeader>
              <CardContent>
                {leaderboard.top_brokers.length === 0 ? (
                  <div className="text-sm text-muted-foreground py-6 text-center">No transactions yet</div>
                ) : leaderboard.top_brokers.map((b, i) => (
                  <div key={b.email} className="flex items-center justify-between py-2 border-b border-border last:border-0">
                    <div className="flex items-center gap-3 min-w-0">
                      <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${i === 0 ? 'bg-amber-500/20 text-amber-400' : 'bg-muted text-muted-foreground'}`}>{i + 1}</div>
                      <div className="min-w-0">
                        <div className="font-medium text-sm truncate">{b.name || b.email}</div>
                        <div className="text-xs text-muted-foreground truncate">{b.loads} loads</div>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="font-bold text-green-400">{fmt(b.gmv)}</div>
                      <div className="text-xs text-muted-foreground">{fmt(b.fees)} fees</div>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
            <Card data-testid="top-drivers">
              <CardHeader><CardTitle className="flex items-center gap-2"><ArrowUpRight className="w-5 h-5" /> Top Drivers (by earnings)</CardTitle></CardHeader>
              <CardContent>
                {leaderboard.top_drivers.length === 0 ? (
                  <div className="text-sm text-muted-foreground py-6 text-center">No transactions yet</div>
                ) : leaderboard.top_drivers.map((d, i) => (
                  <div key={d.email} className="flex items-center justify-between py-2 border-b border-border last:border-0">
                    <div className="flex items-center gap-3 min-w-0">
                      <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${i === 0 ? 'bg-amber-500/20 text-amber-400' : 'bg-muted text-muted-foreground'}`}>{i + 1}</div>
                      <div className="min-w-0">
                        <div className="font-medium text-sm truncate">{d.name || d.email}</div>
                        <div className="text-xs text-muted-foreground truncate">{d.loads} loads</div>
                      </div>
                    </div>
                    <div className="font-bold text-green-400">{fmt(d.earnings)}</div>
                  </div>
                ))}
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default RevenueCommandCenter;
